import React, { useState, useRef } from "react";
import { API_BASE_URL } from "../../../services/api";
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Image,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaImage,
  FaUserCircle,
  FaChevronDown,
  FaChevronRight,
  FaTrash,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../../components/common/Header';
import BlogService from '../../../services/blogService';

const CreateBlog = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [showSeo, setShowSeo] = useState(false);

  // Image states
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState('');
  const [uploadingFeatured, setUploadingFeatured] = useState(false);

  const [authorImage, setAuthorImage] = useState(null);
  const [authorImagePreview, setAuthorImagePreview] = useState('');
  const [uploadingAuthor, setUploadingAuthor] = useState(false);

  const [galleryImages, setGalleryImages] = useState([]);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    title: '',
    category: 'blog',
    short_description: '',
    content: '',
    author_name: '',
    author_bio: '',
    published_date: new Date().toISOString().split('T')[0],
    status: 'draft',
    seo_title: '',
    seo_description: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Upload featured image
  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      toast.error('Please upload JPG, PNG, or WEBP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingFeatured(true);
    setFeaturedImage(file);
    setFeaturedImagePreview(URL.createObjectURL(file));

    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('type', 'blog_featured');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.sensibleacademy.com/backend/admin/blog/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      const data = await response.json();

      if (data.status && data.data) {
        setFormData(prev => ({
          ...prev,
          featured_image: data.data.url,
          featured_image_public_id: data.data.public_id,
        }));
        toast.success('Featured image uploaded successfully');
      } else {
        toast.error(data.message || 'Upload failed');
        setFeaturedImage(null);
        setFeaturedImagePreview('');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
      setFeaturedImage(null);
      setFeaturedImagePreview('');
    } finally {
      setUploadingFeatured(false);
    }
  };

  // Upload author image
  const handleAuthorImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      toast.error('Please upload JPG, PNG, or WEBP image');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size must be less than 2MB');
      return;
    }

    setUploadingAuthor(true);
    setAuthorImage(file);
    setAuthorImagePreview(URL.createObjectURL(file));

    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('type', 'blog_author');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/blog/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      const data = await response.json();

      if (data.status && data.data) {
        setFormData(prev => ({
          ...prev,
          author_image: data.data.url,
          author_image_public_id: data.data.public_id,
        }));
        toast.success('Author image uploaded successfully');
      } else {
        toast.error(data.message || 'Upload failed');
        setAuthorImage(null);
        setAuthorImagePreview('');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload author image');
      setAuthorImage(null);
      setAuthorImagePreview('');
    } finally {
      setUploadingAuthor(false);
    }
  };

  // Upload gallery image
  const handleGalleryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      toast.error('Please upload JPG, PNG, or WEBP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingGallery(true);

    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('type', 'blog_gallery');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/admin/blog/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      const data = await response.json();

      if (data.status && data.data) {
        const newGalleryImage = {
          url: data.data.url,
          public_id: data.data.public_id,
          caption: '',
        };
        setGalleryImages([...galleryImages, newGalleryImage]);
        toast.success('Gallery image uploaded successfully');
      } else {
        toast.error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload gallery image');
    } finally {
      setUploadingGallery(false);
      e.target.value = '';
    }
  };

  const removeGalleryImage = (index) => {
    const newGallery = [...galleryImages];
    newGallery.splice(index, 1);
    setGalleryImages(newGallery);
  };

  const updateGalleryCaption = (index, caption) => {
    const newGallery = [...galleryImages];
    newGallery[index].caption = caption;
    setGalleryImages(newGallery);
  };

  const removeFeaturedImage = () => {
    setFeaturedImage(null);
    setFeaturedImagePreview('');
    setFormData(prev => ({
      ...prev,
      featured_image: '',
      featured_image_public_id: '',
    }));
  };

  const removeAuthorImage = () => {
    setAuthorImage(null);
    setAuthorImagePreview('');
    setFormData(prev => ({
      ...prev,
      author_image: '',
      author_image_public_id: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        gallery_images: galleryImages,
      };

      const response = await BlogService.adminCreateBlog(submitData);
      toast.success('Blog post created successfully!');
      navigate('/admin/blog');
    } catch (error) {
      console.error('Create failed:', error);
      toast.error(error.response?.data?.message || 'Failed to create blog post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      <Container fluid className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button
              variant="link"
              className="text-decoration-none mb-2 p-0"
              onClick={() => navigate('/admin/blog')}
            >
              <FaArrowLeft className="me-2" /> Back to Blog
            </Button>
            <h2 className="mb-0">Create New Blog Post</h2>
            <p className="text-muted">Fill in the details below to create a new blog post</p>
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={8}>
              {/* Basic Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">📝 Basic Information</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter blog title"
                      required
                    />
                    <Form.Text className="text-muted">
                      Slug will be auto-generated from the title
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                    >
                      <option value="blog">Blog</option>
                      <option value="news">News</option>
                      <option value="announcement">Announcement</option>
                      <option value="event">Event</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Short Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="short_description"
                      rows={3}
                      value={formData.short_description}
                      onChange={handleChange}
                      placeholder="Brief description shown on blog cards"
                    />
                    <Form.Text className="text-muted">
                      Max 150-200 characters. Appears on blog listing page.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Content (HTML supported)</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="content"
                      rows={10}
                      value={formData.content}
                      onChange={handleChange}
                      placeholder="Full blog content. HTML supported: &lt;h2&gt;Heading&lt;/h2&gt;, &lt;p&gt;Paragraph&lt;/p&gt;, &lt;ul&gt;&lt;li&gt;List&lt;/li&gt;&lt;/ul&gt;"
                    />
                    <Form.Text className="text-muted">
                      You can use HTML tags for formatting
                    </Form.Text>
                  </Form.Group>

                  {/* Featured Image */}
                  <Form.Group className="mb-3">
                    <Form.Label>Featured Image</Form.Label>
                    <div className="border rounded p-3 text-center">
                      {featuredImagePreview ? (
                        <div className="mb-3">
                          <Image
                            src={featuredImagePreview}
                            alt="Featured preview"
                            style={{ maxHeight: '200px', maxWidth: '100%' }}
                            className="rounded"
                          />
                          <div className="mt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeFeaturedImage}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <FaImage size={50} className="text-muted mb-2" />
                          <p className="text-muted">No image selected</p>
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFeaturedImageUpload}
                        disabled={uploadingFeatured}
                      />
                      {uploadingFeatured && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" /> Uploading...
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Recommended size: 1200x630px. Max 5MB.
                      </Form.Text>
                    </div>
                  </Form.Group>

                  {/* Gallery Images */}
                  <Form.Group className="mb-3">
                    <Form.Label>Gallery Images</Form.Label>
                    <div className="border rounded p-3">
                      <div className="mb-3">
                        <Form.Control
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={handleGalleryImageUpload}
                          disabled={uploadingGallery}
                        />
                        {uploadingGallery && (
                          <div className="mt-2">
                            <Spinner animation="border" size="sm" /> Uploading...
                          </div>
                        )}
                      </div>
                      {galleryImages.length > 0 && (
                        <Row className="g-3">
                          {galleryImages.map((img, index) => (
                            <Col md={4} key={index}>
                              <div className="position-relative">
                                <Image
                                  src={img.url}
                                  alt="Gallery"
                                  className="img-fluid rounded"
                                  style={{ height: '150px', width: '100%', objectFit: 'cover' }}
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="position-absolute top-0 end-0 m-1"
                                  onClick={() => removeGalleryImage(index)}
                                >
                                  <FaTrash />
                                </Button>
                                <Form.Control
                                  type="text"
                                  placeholder="Caption"
                                  size="sm"
                                  className="mt-1"
                                  value={img.caption}
                                  onChange={(e) => updateGalleryCaption(index, e.target.value)}
                                />
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}
                      <Form.Text className="text-muted">
                        Add multiple images to create a gallery. Recommended size: 800x600px.
                      </Form.Text>
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* Author Information */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">👨‍💻 Author Information</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Author Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="author_name"
                      value={formData.author_name}
                      onChange={handleChange}
                      placeholder="Enter author name"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Author Bio</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="author_bio"
                      rows={3}
                      value={formData.author_bio}
                      onChange={handleChange}
                      placeholder="Brief description about the author"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Author Image</Form.Label>
                    <div className="border rounded p-3 text-center">
                      {authorImagePreview ? (
                        <div className="mb-3">
                          <Image
                            src={authorImagePreview}
                            alt="Author preview"
                            style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                            className="rounded-circle"
                          />
                          <div className="mt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeAuthorImage}
                            >
                              Remove Image
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3">
                          <FaUserCircle size={50} className="text-muted mb-2" />
                          <p className="text-muted">No image selected</p>
                        </div>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleAuthorImageUpload}
                        disabled={uploadingAuthor}
                      />
                      {uploadingAuthor && (
                        <div className="mt-2">
                          <Spinner animation="border" size="sm" /> Uploading...
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Recommended: Square image, 150x150px. Max 2MB.
                      </Form.Text>
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              {/* Publish Settings */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-info text-white">
                  <h5 className="mb-0">⚙️ Publish Settings</h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="draft">📝 Draft</option>
                      <option value="published">✅ Published</option>
                      <option value="archived">📦 Archived</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Publish Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="published_date"
                      value={formData.published_date}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      Set to future date for scheduled publishing
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* SEO Section */}
              <Card className="mb-4 shadow-sm">
                <Card.Header
                  className="bg-secondary text-white"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowSeo(!showSeo)}
                >
                  <h5 className="mb-0">
                    {showSeo ? <FaChevronDown className="me-2" /> : <FaChevronRight className="me-2" />}
                    🔍 SEO Information (Optional)
                  </h5>
                </Card.Header>
                {showSeo && (
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>SEO Title</Form.Label>
                      <Form.Control
                        type="text"
                        name="seo_title"
                        value={formData.seo_title}
                        onChange={handleChange}
                        placeholder="Custom title for search engines"
                      />
                      <Form.Text className="text-muted">
                        If empty, blog title will be used
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>SEO Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="seo_description"
                        rows={3}
                        value={formData.seo_description}
                        onChange={handleChange}
                        placeholder="Meta description for search engines (150-160 characters)"
                      />
                      <Form.Text className="text-muted">
                        If empty, short description will be used
                      </Form.Text>
                    </Form.Group>
                  </Card.Body>
                )}
              </Card>

              {/* Action Buttons */}
              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={submitting}
                    >
                      <FaSave className="me-2" />
                      {submitting ? 'Creating...' : 'Create Blog Post'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="lg"
                      onClick={() => navigate('/admin/blog')}
                    >
                      <FaTimes className="me-2" /> Cancel
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default CreateBlog;